/**
 * As 39 cartas de "F total ou Legaaaal" (19 Legaaaal, 14 F total, 6 Caos) e seus efeitos.
 * No baralho, 'sorte' = Legaaaal e 'reves' = F total. Os textos de tela ficam no cliente.
 */
import { RESORT_SQUARES, priceOf, valueOf } from './board';
import {
  type Ctx,
  addEffect,
  charge,
  credit,
  destroyHouse,
  destructibleSquares,
  discardCard,
  emit,
  keepCard,
  moveForwardTo,
  sendToJail,
  setOwner,
  teleport,
  transfer,
  untilAfterRounds,
  useHeld,
} from './core';
import { completeGroupsOf, getPlayer, ownedBy, ownsFullGroup, poorest, richest, worth } from './queries';
import { BOARD_SIZE, HAND_LIMIT, PASS_START_BONUS } from './rules';
import { pick } from './rng';
import type { Choice, GameState, HeldCard, Level, PlayerId, Prompt } from './types';

export type Deck = 'sorte' | 'reves' | 'caos';

export interface CardDef {
  id: string;
  deck: Deck;
  title: string;
  /** Fica na mão do jogador até ser usada. */
  held?: HeldCard;
  /** Destrói construções de outro jogador (limite de 1 por jogador por rodada). */
  destructive?: boolean;
  /** Ataca um oponente escolhido (pode ser bloqueada pelo Seguro Anti-Zoeira). */
  attack?: boolean;
}

export const CARDS: readonly CardDef[] = [
  // Sorte (18)
  { id: 'facaChorar', deck: 'sorte', title: 'Parabéns, faça seu amigo chorar', destructive: true, attack: true },
  { id: 'pixErrado', deck: 'sorte', title: 'Pix errado', attack: true },
  { id: 'fiscal', deck: 'sorte', title: 'Fiscal da Prefeitura', attack: true },
  { id: 'heranca', deck: 'sorte', title: 'Herança da tia-avó que ninguém conhecia' },
  { id: 'vaquinha', deck: 'sorte', title: 'Vaquinha do seu aniversário' },
  { id: 'propagandaBet', deck: 'sorte', title: 'Fez propaganda de bet' },
  { id: 'compraForcada', deck: 'sorte', title: 'Compra forçada', attack: true },
  { id: 'carona', deck: 'sorte', title: 'Carona grátis' },
  { id: 'trocaLugar', deck: 'sorte', title: 'Troca de lugar' },
  { id: 'advogado', deck: 'sorte', title: 'Advogado de porta de cadeia', held: 'advogado' },
  { id: 'seguro', deck: 'sorte', title: 'Seguro Anti-Zoeira', held: 'seguro' },
  { id: 'reforma', deck: 'sorte', title: 'Reforma no capricho' },
  { id: 'blackFriday', deck: 'sorte', title: 'Black Friday', held: 'blackFriday' },
  { id: 'cupom', deck: 'sorte', title: 'Cupom de desconto', held: 'cupom' },
  { id: 'achouDinheiro', deck: 'sorte', title: 'Achou dinheiro na calça' },
  { id: 'impostoBilionario', deck: 'sorte', title: 'Imposto do bilionário' },
  { id: 'viralizou', deck: 'sorte', title: 'Viralizou' },
  { id: 'voltaInicio', deck: 'sorte', title: 'Volta ao início' },
  { id: 'ladraoGalinha', deck: 'sorte', title: 'Ladrão de galinha', attack: true },
  { id: 'fazendeiro', deck: 'sorte', title: 'Carta Fazendeiro' },
  // Revés (14)
  { id: 'multaRadar', deck: 'reves', title: 'Multa do radar' },
  { id: 'golpeParente', deck: 'reves', title: 'Golpe do falso parente' },
  { id: 'boletoEsquecido', deck: 'reves', title: 'Boleto esquecido' },
  { id: 'camburao', deck: 'reves', title: 'Camburão' },
  { id: 'cupim', deck: 'reves', title: 'Cupim' },
  { id: 'manutencao', deck: 'reves', title: 'Manutenção geral' },
  { id: 'rodizio', deck: 'reves', title: 'Rodízio por sua conta' },
  { id: 'exApareceu', deck: 'reves', title: 'O ex apareceu' },
  { id: 'esqueceuCarteira', deck: 'reves', title: 'Esqueceu a carteira' },
  { id: 'leaoReceita', deck: 'reves', title: 'Leão da Receita' },
  { id: 'celularPrivada', deck: 'reves', title: 'Celular caiu na privada' },
  { id: 'condominio', deck: 'reves', title: 'Condomínio atrasado' },
  { id: 'pomboAntena', deck: 'reves', title: 'Pombo na antena' },
  { id: 'busaoQuebrou', deck: 'reves', title: 'Busão quebrou' },
  // Caos (6) — entram no baralho no nível de tensão 3
  { id: 'terremoto', deck: 'caos', title: 'Terremoto' },
  { id: 'apocalipse', deck: 'caos', title: 'Apocalipse Zoeiro', destructive: true, attack: true },
  { id: 'apagao', deck: 'caos', title: 'Apagão' },
  { id: 'inflacao', deck: 'caos', title: 'Inflação' },
  { id: 'calote', deck: 'caos', title: 'Calote', held: 'calote' },
  { id: 'assalto', deck: 'caos', title: 'Assalto à banca' },
];

export const CARD_BY_ID: Record<string, CardDef> = Object.fromEntries(CARDS.map((c) => [c.id, c]));
export const BASE_DECK: readonly string[] = CARDS.filter((c) => c.deck !== 'caos').map((c) => c.id);
export const CAOS_DECK: readonly string[] = CARDS.filter((c) => c.deck === 'caos').map((c) => c.id);

export function cardDef(id: string): CardDef {
  const c = CARD_BY_ID[id];
  if (!c) throw new Error(`Carta desconhecida: ${id}`);
  return c;
}

// ---------------------------------------------------------------------------
// Alvos possíveis

const opponents = (s: GameState, me: PlayerId) => s.players.filter((p) => p.id !== me && !p.eliminated);

/** Jogador ainda pode sofrer ataque destrutivo nesta rodada? */
export function canSufferDestruction(s: GameState, player: PlayerId): boolean {
  return s.attackedRound[player] !== s.round;
}

const sqChoice = (square: number, player?: PlayerId): Choice => ({ id: `sq:${square}`, square, ...(player ? { player } : {}) });
const pChoice = (player: PlayerId): Choice => ({ id: `p:${player}`, player });

function destructionChoices(s: GameState, me: PlayerId, exclude: number[] = []): Choice[] {
  const out: Choice[] = [];
  for (const op of opponents(s, me)) {
    if (!canSufferDestruction(s, op.id)) continue;
    for (const i of destructibleSquares(s, op.id)) if (!exclude.includes(i)) out.push(sqChoice(i, op.id));
  }
  return out;
}

export function choicesFor(s: GameState, me: PlayerId, card: string): Choice[] {
  const meP = getPlayer(s, me);
  switch (card) {
    case 'facaChorar':
    case 'apocalipse':
      return destructionChoices(s, me);
    case 'pixErrado':
      return opponents(s, me).filter((p) => p.cash > 0).map((p) => pChoice(p.id));
    case 'fiscal':
      // Hotel ninguém mexe: nem toma, nem derruba, nem interdita.
      return opponents(s, me).flatMap((op) =>
        ownedBy(s, op.id)
          .filter((i) => s.props[i]!.level < 4)
          .map((i) => sqChoice(i, op.id)),
      );
    case 'compraForcada':
      // Só cidade com bandeirola (sem casas), fora de grupo completo do dono.
      return opponents(s, me).flatMap((op) =>
        ownedBy(s, op.id)
          .filter((i) => {
            const o = s.props[i]!;
            if (o.level !== 0 || priceOf(i) === 0 || RESORT_SQUARES.includes(i)) return false;
            if (ownsFullGroup(s, op.id, i)) return false;
            return meP.cash >= forcedPrice(i);
          })
          .map((i) => sqChoice(i, op.id)),
      );
    case 'carona':
      return RESORT_SQUARES.filter((i) => i !== meP.position).map((i) => sqChoice(i));
    case 'trocaLugar':
      if (meP.inJail) return [];
      return opponents(s, me).filter((p) => !p.inJail).map((p) => pChoice(p.id));
    case 'ladraoGalinha':
      if (meP.held.length + (meP.shop?.length ?? 0) >= HAND_LIMIT) return [];
      return opponents(s, me).filter((p) => p.held.length > 0).map((p) => pChoice(p.id));
    case 'reforma':
      // Uma casa grátis numa cidade sua com bandeirola, casa 1 ou casa 2.
      return ownedBy(s, me)
        .filter((i) => !RESORT_SQUARES.includes(i) && s.props[i]!.level <= 2)
        .map((i) => sqChoice(i));
    case 'cupim':
      return destructibleSquares(s, me).map((i) => sqChoice(i));
    case 'fazendeiro':
      // Protege uma cidade sua do MST para sempre.
      return ownedBy(s, me)
        .filter((i) => !RESORT_SQUARES.includes(i) && !(s.farms ?? []).includes(i))
        .map((i) => sqChoice(i));
    default:
      return [];
  }
}

export const CHOICE_CARDS = new Set([
  'facaChorar',
  'apocalipse',
  'pixErrado',
  'fiscal',
  'compraForcada',
  'carona',
  'trocaLugar',
  'ladraoGalinha',
  'reforma',
  'cupim',
  'fazendeiro',
]);

export function forcedPrice(index: number): number {
  return Math.ceil(valueOf(index, 0) * 1.5);
}

// ---------------------------------------------------------------------------
// Resolução

function finishCard(ctx: Ctx): void {
  const card = ctx.s.pendingCard;
  if (card) discardCard(ctx, card);
  ctx.s.pendingCard = null;
  ctx.s.prompt = null;
}

function noEffect(ctx: Ctx, player: PlayerId, card: string): void {
  emit(ctx, { type: 'cardNoEffect', player, card });
  finishCard(ctx);
}

/** Seguro Anti-Zoeira: se o alvo tiver, o ataque é bloqueado e a carta é gasta. */
function shielded(ctx: Ctx, target: PlayerId, attacker: PlayerId, card: string): boolean {
  if (useHeld(ctx, target, 'seguro')) {
    emit(ctx, { type: 'shieldBlocked', target, attacker, card });
    return true;
  }
  return false;
}

function countAttack(ctx: Ctx, attacker: PlayerId, target: PlayerId): void {
  getPlayer(ctx.s, attacker).stats.attacksMade += 1;
  getPlayer(ctx.s, target).stats.attacksReceived += 1;
}

/** Resolve a carta recém-puxada (que está em s.pendingCard). */
export function resolveCard(ctx: Ctx, player: PlayerId, card: string): void {
  const s = ctx.s;
  const def = cardDef(card);
  const p = getPlayer(s, player);

  if (def.held) {
    s.pendingCard = null;
    keepCard(ctx, player, def.held);
    return;
  }

  if (CHOICE_CARDS.has(card)) {
    const choices = choicesFor(s, player, card);
    if (choices.length === 0) return noEffect(ctx, player, card);
    s.prompt = {
      kind: 'card',
      player,
      card,
      choices,
      canSkip: card === 'compraForcada',
      picksLeft: card === 'apocalipse' ? 2 : 1,
      picked: [],
    };
    return;
  }

  const others = opponents(s, player).map((o) => o.id);
  switch (card) {
    case 'heranca':
      credit(ctx, player, 200, card);
      break;
    case 'achouDinheiro':
      credit(ctx, player, 50, card);
      break;
    case 'vaquinha':
      for (const o of others) charge(ctx, o, player, 50, card);
      break;
    case 'propagandaBet':
      for (const o of others) charge(ctx, o, player, 75, card);
      break;
    case 'impostoBilionario': {
      const rich = richest(s);
      const poor = poorest(s);
      if (rich !== poor) charge(ctx, rich, poor, 150, card);
      break;
    }
    case 'viralizou':
      addEffect(ctx, { kind: 'viralizou', player, untilTurn: untilAfterRounds(s, 1) });
      break;
    case 'voltaInicio':
      teleport(ctx, player, 0);
      p.laps += 1;
      credit(ctx, player, PASS_START_BONUS, 'inicio');
      emit(ctx, { type: 'passedStart', player, amount: PASS_START_BONUS });
      break;
    case 'multaRadar':
      charge(ctx, player, 'bank', 100, card);
      break;
    case 'golpeParente':
      charge(ctx, player, 'bank', 150, card);
      break;
    case 'boletoEsquecido':
      charge(ctx, player, 'bank', Math.max(50, Math.floor(p.cash / 10)), card);
      break;
    case 'camburao':
      sendToJail(ctx, player, card);
      break;
    case 'manutencao': {
      let total = 0;
      for (const i of ownedBy(s, player)) {
        const l = s.props[i]!.level;
        total += l === 4 ? 100 : l * 25;
      }
      if (total === 0) return noEffect(ctx, player, card);
      charge(ctx, player, 'bank', total, card);
      break;
    }
    case 'rodizio':
      for (const o of others) charge(ctx, player, o, 50, card);
      break;
    case 'exApareceu':
      charge(ctx, player, poorest(s, others), 100, card);
      break;
    case 'esqueceuCarteira':
      teleport(ctx, player, (p.position - 3 + BOARD_SIZE) % BOARD_SIZE, 'back');
      s.queue.unshift({ k: 'land', player });
      break;
    case 'leaoReceita':
      charge(ctx, player, 'bank', Math.min(200, Math.floor(worth(s, player) / 10)), card);
      break;
    case 'celularPrivada':
      if (p.held.length > 0) {
        const lost = pick(s, p.held);
        p.held.splice(p.held.indexOf(lost), 1);
        discardCard(ctx, lost);
        emit(ctx, { type: 'cardLost', player, card: lost });
      }
      charge(ctx, player, 'bank', 50, card);
      break;
    case 'condominio': {
      const groups = completeGroupsOf(s, player).length;
      if (groups === 0) return noEffect(ctx, player, card);
      charge(ctx, player, 'bank', 75 * groups, card);
      break;
    }
    case 'pomboAntena': {
      const mine = ownedBy(s, player).sort((a, b) => priceOf(b) - priceOf(a) || b - a);
      const target = mine[0];
      if (target === undefined) return noEffect(ctx, player, card);
      const until = untilAfterRounds(s, 1);
      s.effects.push({ kind: 'interdicted', square: target, untilTurn: until });
      emit(ctx, { type: 'interdicted', square: target, untilTurn: until });
      break;
    }
    case 'busaoQuebrou':
      addEffect(ctx, { kind: 'busao', player });
      break;
    case 'terremoto': {
      let any = false;
      for (const pl of opponents(s, '')) {
        const target = destructibleSquares(s, pl.id).sort(
          (a, b) => s.props[b]!.level - s.props[a]!.level || priceOf(b) - priceOf(a),
        )[0];
        if (target !== undefined) {
          destroyHouse(ctx, target, null, card);
          any = true;
        }
      }
      if (!any) return noEffect(ctx, player, card);
      break;
    }
    case 'apagao':
      addEffect(ctx, { kind: 'apagao', untilTurn: untilAfterRounds(s, 1) });
      break;
    case 'inflacao':
      addEffect(ctx, { kind: 'inflacao', untilTurn: untilAfterRounds(s, 2) });
      break;
    case 'assalto': {
      const rich = richest(s);
      credit(ctx, player, 300, card);
      charge(ctx, rich, 'bank', 100, card);
      break;
    }
    default:
      throw new Error(`Carta sem efeito implementado: ${card}`);
  }
  finishCard(ctx);
}

/** Aplica a escolha feita para uma carta com alvo. */
export function applyCardChoice(ctx: Ctx, prompt: Extract<Prompt, { kind: 'card' }>, choiceId: string): void {
  const s = ctx.s;
  const me = prompt.player;
  const card = prompt.card;

  if (choiceId === 'skip') {
    if (!prompt.canSkip) throw new Error('Esta carta não pode ser pulada');
    finishCard(ctx);
    return;
  }
  const choice = prompt.choices.find((c) => c.id === choiceId);
  if (!choice) throw new Error(`Escolha inválida: ${choiceId}`);

  switch (card) {
    case 'facaChorar':
    case 'apocalipse': {
      const target = choice.player!;
      const sqIdx = choice.square!;
      if (!shielded(ctx, target, me, card)) {
        destroyHouse(ctx, sqIdx, me, card);
        countAttack(ctx, me, target);
      }
      prompt.picked.push(sqIdx);
      prompt.picksLeft -= 1;
      if (card === 'apocalipse' && prompt.picksLeft > 0) {
        const next = destructionChoices(s, me, prompt.picked);
        if (next.length > 0) {
          prompt.choices = next;
          prompt.canSkip = true;
          return; // ainda falta a segunda escolha
        }
      }
      // O ataque destrutivo conta para cada alvo atingido, depois da carta inteira.
      for (const sqI of prompt.picked) {
        const owner = s.props[sqI]?.owner;
        if (owner && owner !== me) s.attackedRound[owner] = s.round;
      }
      break;
    }
    case 'pixErrado': {
      const target = choice.player!;
      if (!shielded(ctx, target, me, card)) {
        const amount = Math.min(100, getPlayer(s, target).cash);
        transfer(ctx, target, me, amount, card);
        emit(ctx, { type: 'paid', from: target, to: me, amount, reason: card });
        countAttack(ctx, me, target);
      }
      break;
    }
    case 'fiscal': {
      const target = choice.player!;
      if (!shielded(ctx, target, me, card)) {
        const until = untilAfterRounds(s, 1);
        s.effects.push({ kind: 'interdicted', square: choice.square!, untilTurn: until });
        emit(ctx, { type: 'interdicted', square: choice.square!, untilTurn: until });
        countAttack(ctx, me, target);
      }
      break;
    }
    case 'compraForcada': {
      const target = choice.player!;
      const sqIdx = choice.square!;
      if (!shielded(ctx, target, me, card)) {
        const price = forcedPrice(sqIdx);
        transfer(ctx, me, target, price, card);
        setOwner(ctx, me, sqIdx);
        emit(ctx, { type: 'forcedPurchase', buyer: me, from: target, square: sqIdx, price });
        countAttack(ctx, me, target);
      }
      break;
    }
    case 'carona':
      moveForwardTo(ctx, me, choice.square!);
      s.queue.unshift({ k: 'land', player: me });
      break;
    case 'trocaLugar': {
      const other = getPlayer(s, choice.player!);
      const meP = getPlayer(s, me);
      const a = meP.position;
      const b = other.position;
      meP.position = b;
      other.position = a;
      emit(ctx, { type: 'swapped', a: me, b: other.id });
      // Quem foi levado resolve primeiro (só paga), para não esperar a decisão de quem puxou a carta.
      s.queue.unshift({ k: 'passiveLand', player: other.id }, { k: 'land', player: me });
      break;
    }
    case 'ladraoGalinha': {
      const target = choice.player!;
      if (!shielded(ctx, target, me, card)) {
        const victim = getPlayer(s, target);
        const stolen = pick(s, victim.held);
        victim.held.splice(victim.held.indexOf(stolen), 1);
        getPlayer(s, me).held.push(stolen);
        emit(ctx, { type: 'cardStolen', from: target, to: me, card: stolen });
        countAttack(ctx, me, target);
      }
      break;
    }
    case 'reforma': {
      const o = s.props[choice.square!]!;
      const from = o.level;
      o.level = (o.level + 1) as Level;
      emit(ctx, { type: 'built', player: me, square: choice.square!, from, level: o.level, cost: 0 });
      break;
    }
    case 'cupim':
      destroyHouse(ctx, choice.square!, null, card);
      break;
    case 'fazendeiro':
      (s.farms ??= []).push(choice.square!);
      emit(ctx, { type: 'farmProtected', player: me, square: choice.square! });
      break;
    default:
      throw new Error(`Carta sem escolha: ${card}`);
  }
  finishCard(ctx);
}
